import React, { useCallback, useId } from 'react'
import { EmojiPickerButton, EmojiPickerButtonTouch } from '@components/EmojiPickerButton'
import { GiphyEntryDesktop, GiphyEntryTouch } from '@components/Giphy/GiphyEntry'
import { useMediaDropContext } from '@components/MediaDropContext/MediaDropContext'
import { useEditorRef, useEventEditorSelectors } from '@udecode/plate-common/react'
import { Box, IconButton, Stack } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { MotionIcon, MotionIconButton } from 'ui/components/Motion/MotionComponents'

import { ELEMENT_MENTION_EMOJI } from '../plugins/createEmojiPlugin'
import { focusEditorTowns } from '../utils/helpers'

import { TEmojiMentionElement } from './plate-ui/autocomplete/types'

type Props = {
    threadId?: string
    threadPreview?: string
    visible: boolean
    editing?: boolean
    focused?: boolean
    isFormattingToolbarOpen: boolean
    setIsFormattingToolbarOpen: (isFormattingToolbarOpen: boolean) => void
}

export const EditorToolbarBottom = (props: Props) => {
    const { isTouch } = useDevice()
    const editor = useEditorRef()
    const mediaDropContext = useMediaDropContext()

    const {
        isFormattingToolbarOpen,
        setIsFormattingToolbarOpen,
        editing: isEditing = false,
        focused: isFocused = false,
    } = props

    const onSelectEmoji = useCallback(
        (data: EmojiPickerSelection) => {
            editor.insertNodes([
                {
                    type: ELEMENT_MENTION_EMOJI,
                    children: [{ text: data.native }],
                    emoji: { name: data.name, emoji: data.native },
                    value: data.native,
                } as TEmojiMentionElement,
                { text: ' ' },
            ])
            focusEditorTowns(editor, true)
        },
        [editor],
    )

    const onFormattingButtonClicked = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            setIsFormattingToolbarOpen(!isFormattingToolbarOpen)
            focusEditorTowns(editor)
        },
        [setIsFormattingToolbarOpen, editor, isFormattingToolbarOpen],
    )

    const didSelectImages = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const fileList = event.target.files
            if (!fileList) {
                return
            }
            const files = Array.from(fileList)
            mediaDropContext.addFiles?.(files)
            event.target.value = ''
        },
        [mediaDropContext],
    )

    const mediaInputId = useId()

    return (
        <Stack
            horizontal
            gap={isTouch ? 'sm' : 'xs'}
            alignItems="center"
            data-testid="editor-toolbar-bottom"
        >
            {isTouch ? (
                <>
                    {props.visible && (
                        <MotionIconButton
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            icon="text"
                            active={false}
                            onMouseDown={onFormattingButtonClicked}
                        />
                    )}
                    {!isEditing && (
                        <GiphyEntryTouch
                            key="giphy"
                            threadId={props.threadId}
                            threadPreview={props.threadPreview}
                            showButton={props.visible}
                        />
                    )}
                    <EmojiPickerButtonTouch
                        key="emoji"
                        showButton={props.visible}
                        onSelectEmoji={onSelectEmoji}
                    />
                </>
            ) : (
                <>
                    <IconButton
                        icon="text"
                        active={false}
                        tooltip="Formatting"
                        tooltipOptions={{ placement: 'vertical', immediate: true }}
                        data-testid="text-formatting-button"
                        onClick={onFormattingButtonClicked}
                    />
                    {!isEditing ? (
                        <GiphyEntryDesktop
                            parentFocused={isFocused}
                            threadId={props.threadId}
                            threadPreview={props.threadPreview}
                        />
                    ) : null}

                    <EmojiPickerButton
                        key="emoji-non-touch"
                        parentFocused={useEventEditorSelectors.focus() === editor.id && isFocused}
                        onSelectEmoji={onSelectEmoji}
                    />
                </>
            )}

            <label htmlFor={mediaInputId}>
                {(!isTouch || props.visible) && !isEditing && (
                    <Box
                        padding="xs"
                        tooltip={isTouch ? undefined : 'Upload file'}
                        tooltipOptions={{ immediate: true, placement: 'vertical' }}
                        data-testid="add-attachment-button"
                    >
                        <MotionIcon
                            type="attachment"
                            size="square_sm"
                            color={{ hover: 'default', default: 'gray2' }}
                            cursor="pointer"
                            initial={isTouch ? { opacity: 0 } : undefined}
                            animate={isTouch ? { opacity: 1 } : undefined}
                            exit={isTouch ? { opacity: 0 } : undefined}
                        />
                    </Box>
                )}
                <input
                    multiple
                    type="file"
                    name="file-input"
                    id={mediaInputId}
                    accept="*"
                    style={{ display: 'none' }}
                    onChange={didSelectImages}
                />
            </label>
        </Stack>
    )
}
